/**
 * Save helpers for the Vectorize tool. Two destinations, picked by
 * the in-editor `VectorizeSaveDialog`:
 *
 *   • Local download — anchor-element download. Browser drops the
 *     file in the user's default download folder. No OS-native save
 *     dialog: we deliberately don't use `showSaveFilePicker` here
 *     because it requires a user gesture that has already been
 *     consumed by the API call earlier in the flow, and matching the
 *     ExportDialog's local-download behavior keeps the editor's
 *     two save dialogs visually + functionally consistent. iOS Safari
 *     gets the share-sheet workaround the existing useImageExport
 *     hook also uses — it ignores `download` on anchors otherwise.
 *
 *   • Fias files — the canonical 3-step upload flow:
 *       1. `api.getUploadUrl({ fileName, mimeType, sizeBytes })` →
 *          presigned PUT URL + the prospective fileId / s3Key
 *       2. PUT the bytes to the presigned URL directly (skips our
 *          API; goes straight to S3)
 *       3. `api.confirmUpload(fileId, ...)` → registers the metadata
 *          row so the file shows up in the user's library
 *     Reuses `category: 'generated'` since vectorize output is
 *     produced by an AI tool — same bucket as Smart Create / Insert
 *     outputs.
 */

import api from '@/lib/api';

export type SaveSvgOutcome = 'saved' | 'failed';

/**
 * Download the SVG locally via an anchor element. Always reports
 * `'saved'` on success — the browser handles the rest of the flow
 * without exposing whether the user cancelled, so we have no way to
 * distinguish saved-vs-cancelled from JS. iOS Safari is special-cased
 * to use the share sheet because it ignores `download=` on anchors.
 */
export async function downloadSvgLocal(svg: string, fileName: string): Promise<SaveSvgOutcome> {
  const fullName = ensureSvgExtension(fileName);
  const blob = new Blob([svg], { type: 'image/svg+xml' });
  try {
    const isIOS =
      typeof navigator !== 'undefined' &&
      /iPad|iPhone|iPod/.test(navigator.userAgent) &&
      !('MSStream' in window);
    if (isIOS && typeof navigator !== 'undefined' && 'share' in navigator) {
      const file = new File([blob], fullName, { type: blob.type });
      const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
      if (!nav.canShare || nav.canShare({ files: [file] })) {
        try {
          await nav.share({ files: [file], title: fullName });
          return 'saved';
        } catch {
          // Share failed (cancel or otherwise) — fall through to
          // anchor download. Cancel on iOS share is indistinguishable
          // from genuine errors, so we just retry the anchor path.
        }
      }
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fullName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return 'saved';
  } catch {
    return 'failed';
  }
}

/**
 * Upload the SVG to Fias files via the standard 3-step presigned-URL
 * flow. Returns the new file's metadata on success. Mirrors the
 * pattern already used by ReferenceImageDialog, ChatSettingsModal,
 * and other consumers.
 */
export async function uploadSvgToFias(svg: string, fileName: string): Promise<SaveSvgOutcome> {
  const fullName = ensureSvgExtension(fileName);
  const blob = new Blob([svg], { type: 'image/svg+xml' });
  try {
    const memberships = await api.listMyVaultMemberships();
    if (!memberships.personalHolder) {
      throw new Error('Personal workspace not provisioned');
    }
    const file = new File([blob], fullName, { type: 'image/svg+xml' });
    await api.uploadFileToVault(file, memberships.personalHolder.holderId, {
      tags: ['image-editor', 'svg'],
    });
    return 'saved';
  } catch (err) {
    console.warn('[image-editor] Fias SVG upload failed:', err);
    return 'failed';
  }
}

function ensureSvgExtension(name: string): string {
  return /\.svg$/i.test(name) ? name : `${name}.svg`;
}
